// Derive the journey set reachable from an entry journey: cumulative days per
// touchpoint, and each sub-journey attached under its LATEST trigger (deduped,
// with earlier triggers recorded as secondaries). Pure.
import { resolveSegmentRefs } from './segment/segmentRead';
import { isoToDays } from '../../utility/isoDuration';

// A journey with no touchpoints still needs one card so the timeline can lay out its
// header/lane and offer an "add first touchpoint" action. This synthetic placeholder is
// presentational only — it is never persisted or counted. See TimelineCanvas for its render.
const placeholderTouchpoint = (journeyId) => ({
  touchpoint_id: `ph-${journeyId}`, placeholder: true, day: 0, isEntry: true, wait_time: 'PT0S', name: '', message: '',
});

// Trigger links, cumulative days and parent→children for the WHOLE forest. Independent of
// which journey is the entry, so callers that need every journey's subtree (gallery
// sub-journey counts) build it once and walk it per journey.
export function deriveForest({ journeys, segments }) {
  const segById = new Map(segments.map((segment) => [segment.segment_id, segment]));
  const tpIndex = {}; // touchpoint_id -> { tp, journey, order }
  let order = 0;
  const tpIdsByResponseAttr = new Map(); // String(response_attribute_id) -> [touchpoint_id]
  journeys.forEach((journey) => journey.touchpoints.forEach((tp) => {
    tpIndex[tp.touchpoint_id] = { tp, journey, order: order++ };
    if (tp.response_attribute_id == null) return;
    const key = String(tp.response_attribute_id);
    if (!tpIdsByResponseAttr.has(key)) tpIdsByResponseAttr.set(key, []);
    tpIdsByResponseAttr.get(key).push(tp.touchpoint_id);
  }));

  // trigger set for a journey = touchpoints whose response attr its segment references
  const triggerCache = new Map();
  const triggerSet = (journey) => {
    if (triggerCache.has(journey.journey_id)) return triggerCache.get(journey.journey_id);
    const refIds = new Set(resolveSegmentRefs(segById.get(journey.segment_id), segById).map((ref) => String(ref.attribute_id)));
    // keep forest order (journeys, then touchpoints) — latestTrigger breaks day ties on it
    const triggers = [...refIds].flatMap((id) => tpIdsByResponseAttr.get(id) || [])
      .sort((a, b) => tpIndex[a].order - tpIndex[b].order);
    triggerCache.set(journey.journey_id, triggers);
    return triggers;
  };

  // cumulative day per touchpoint, resolving journeys in trigger-topological order
  const dayOf = {}; const started = {};
  const resolve = (journey) => {
    if (started[journey.journey_id]) return;
    started[journey.journey_id] = true;                       // set BEFORE recursing so a cycle terminates
    const triggers = triggerSet(journey);
    triggers.forEach((tid) => resolve(tpIndex[tid].journey));
    const known = triggers.map((tid) => dayOf[tid]).filter((day) => day != null);
    const start = known.length ? Math.max(...known) : 0; // ignore triggers whose day is unresolved (cycle)
    let cum = start;
    [...journey.touchpoints].sort((a, b) => a.ordinal - b.ordinal).forEach((tp) => { cum += isoToDays(tp.wait_time); dayOf[tp.touchpoint_id] = cum; });
  };
  journeys.forEach(resolve);

  const latestTrigger = (journey) => { const triggers = triggerSet(journey); return triggers.length ? triggers.reduce((a, b) => (dayOf[a] >= dayOf[b] ? a : b)) : null; };
  const childrenOf = {}; journeys.forEach((journey) => { childrenOf[journey.journey_id] = []; });
  journeys.forEach((journey) => { const latestTriggerId = latestTrigger(journey); if (latestTriggerId) childrenOf[tpIndex[latestTriggerId].journey.journey_id].push(journey); });

  const journeyById = new Map(journeys.map((journey) => [journey.journey_id, journey]));
  return { segById, dayOf, triggerSet, latestTrigger, childrenOf, journeyById };
}

// Pre-order journey ids reachable from `entryJourneyId` (entry first, deduped).
export function subtreeIds(forest, entryJourneyId) {
  const out = []; const seen = new Set();
  const walk = (jid) => {
    if (seen.has(jid)) return; seen.add(jid);
    if (!forest.journeyById.has(jid)) return;
    out.push(jid);
    forest.childrenOf[jid].forEach((child) => walk(child.journey_id));
  };
  walk(entryJourneyId);
  return out;
}

export function deriveModel(entryJourneyId, { journeys, segments }) {
  const forest = deriveForest({ journeys, segments });
  const { segById, dayOf, triggerSet, latestTrigger, journeyById } = forest;

  const built = (journey) => {
    const latestTriggerId = latestTrigger(journey);
    const triggers = triggerSet(journey);
    return {
      journeyId: journey.journey_id, name: journey.name, segment: segById.get(journey.segment_id) || null,
      isRoot: triggers.length === 0, primaryTriggerTpId: latestTriggerId,
      secondaryTriggerTpIds: triggers.filter((tid) => tid !== latestTriggerId),
      touchpoints: (() => {
        const sortedTouchpoints = [...journey.touchpoints].sort((a, b) => a.ordinal - b.ordinal)
          // floor the exact cumulative day to a whole calendar-day index for banding/labels;
          // accumulation itself (dayOf) stays exact so sub-day waits carry precisely.
          .map((tp, i) => ({ ...tp, day: Math.floor(dayOf[tp.touchpoint_id]), isEntry: i === 0 }));
        return sortedTouchpoints.length ? sortedTouchpoints : [placeholderTouchpoint(journey.journey_id)];
      })(),
    };
  };

  return { rootId: entryJourneyId, journeys: subtreeIds(forest, entryJourneyId).map((jid) => built(journeyById.get(jid))) };
}
